// Required as outlined here, https://docs.amplify.aws/react/build-a-backend/auth/add-social-provider/#required-for-multi-page-applications-complete-social-sign-in-after-redirect
// import 'aws-amplify/auth/enable-oauth-listener';

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import { State } from 'redux/state';

import { setToken, setProfile } from 'auth/modules/actions';
import { useAccessToken } from "auth/modules/sagas/WebAuth";
import { replace } from 'connected-react-router';
import { useGetUserProfileQuery } from "generated/graphql";

import { AuthorizeComponent } from '../components/AuthorizeComponent';

/**
 * Wraps the AuthorizeComponent and handles the OAuth2.0 flow redirects
 *
 * Typically this is at /authorize
 *
 * @param props
 * @returns
 */
const AuthorizeContainer: React.FC<RouteComponentProps> = (props) => {

    const clientId = window.xaStudioConfig?.auth?.userWebPoolClientId;

    const dispatch = useDispatch();
    const location = useLocation();

    // const pathname = location.pathname;
    const search = location.search;
    const hash = location.hash;

    const urlHashParams = new URLSearchParams(hash.substring(1));
    let errorDescription = urlHashParams.get('error_description');
    if (!errorDescription) {
        // try to get it from the query string instead, can also be there
        const urlSearchParams = new URLSearchParams(location.search);
        errorDescription = urlSearchParams.get('error_description');
    }

    const searchParams = search.slice(1).split("&");
    let authorizationCode: string;
    let stateParam: string;

    for (const searchParam of searchParams) {
        if (/code=.+/.test(searchParam)) {
            authorizationCode = searchParam.split("=")[1];
        } else if (/state=.+/.test(searchParam)) {
            stateParam = searchParam.split("=")[1];
        }
    }

    // see if we have any state in the hash
    if (!stateParam) {
        const hashParams = hash.slice(1).split("&");
        for (const hashParam of hashParams) {
            if (/code=.+/.test(hashParam)) {
                authorizationCode = hashParam.split("=")[1];
            } else if (/state=.+/.test(hashParam)) {
                stateParam = hashParam.split("=")[1];
            }
        }
    }

    const [loadingText, setLoadingText] = useState<string>("Authorizing...");

    const [error, setError] = useState<Error | undefined>(errorDescription ? new Error(errorDescription) : undefined);

    const { redirectUrl, ...rest } = useSelector((state: State) => ({
        awsId: state.auth.awsId,
        codeVerifier: state.auth.codeVerifier,
        redirectUrl: state.auth.redirectUrl,
        // token: state.auth.,
        state: state.auth.state,
    }));
    // these are mutable since we might change them
    let state = rest.state
    let codeVerifier = rest.codeVerifier;

    // see if we have an inflight oauth state
    // CognitoIdentityServiceProvider.1redi52hv2idkapg4h99dvv5f8.inflightOAuth
    const cognitoInflightOAuth = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.inflightOAuth`);
    if (cognitoInflightOAuth) {
        // update state and codeVerifier
        codeVerifier = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.oauthPKCE`);
        // CognitoIdentityServiceProvider.1redi52hv2idkapg4h99dvv5f8.oauthState
        state = localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.oauthState`);
    }

    if (!error && stateParam !== state) {
        // TODO: stateParam is sometimes "undefined", what do we do here?
        setError(new Error(`State mismatch, received ${stateParam} but expecting ${state}.  This is the result of a security check.`));
    }

    if (!error && !authorizationCode) {
        setError(new Error("No authorization code "));
    }

    const { token, error: tokenError, fetching: fetchingToken } = useAccessToken({
        pause: !!error,
        code: authorizationCode,
        codeVerifier
    });

    const [response] = useGetUserProfileQuery({
        pause: !!error || !token?.accessToken,
        requestPolicy: "network-only", // Only the latest and greatest,
        variables: {}
    });

    const { data, fetching: fetchingProfile, error: profileError } = response;

    const userProfile = data?.profile;

    useEffect(() => {
        if (token) {
            dispatch(setToken({
                accessToken: token.accessToken,
                expiresInSeconds: token.expiresIn,
                moveTo: redirectUrl,
                refreshToken: token.refreshToken,
            }));
        }
    }, [dispatch, redirectUrl, token]);

    useEffect(() => {
        if (userProfile) {
            dispatch(setProfile(userProfile));
        }
    }, [dispatch, userProfile]);

    useEffect(() => {
        if (fetchingToken) {
            setLoadingText("Fetching access token...");
        } else if (fetchingProfile) {
            setLoadingText("Fetching user profile...");
        }
    }, [fetchingProfile, fetchingToken]);

    const handleLoginRequest = () => {
        if (window.location.pathname !== "/login") {
            dispatch(replace("/login"));
        }
    };

    return (
        <AuthorizeComponent
            {...props}
            error={error || tokenError || profileError}
            loadingText={loadingText}
            redirectUrl={redirectUrl}
            profile={userProfile}
            onLoginRequest={handleLoginRequest}
        />
    );
};

export default AuthorizeContainer;